/**
 * Customer Survey Page
 * Public page where customers can fill out surveys without authentication
 * URL: /survey/:campaignId/:uniqueToken/:accessHash
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Rating,
  Alert,
  Card,
  CardContent,
  styled,
  CircularProgress,
} from '@mui/material';
import { Star } from '@mui/icons-material';
import { getCustomerSurvey, submitCustomerSurvey } from '../services/customerSurveyService';
import type {
  CustomerSurveyData,
  CustomerSurveyQuestion,
  CustomerAnswer,
} from '../types/customerSurvey';

// Styled Components
const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: '#F9FAFB',
  padding: theme.spacing(2),
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(5, 10),
  },
  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(5, 20),
  },
  [theme.breakpoints.up('xl')]: {
    padding: theme.spacing(5, 40),
  },
}));

const ContentContainer = styled(Box)({
  maxWidth: '720px',
  margin: '0 auto',
  width: '100%',
});

const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(2, 0),
  marginBottom: theme.spacing(4),
}));

const LogoIcon = styled(Box)({
  width: '32px',
  height: '32px',
  color: '#4F46E5',
});


const CustomerSurvey: React.FC = () => {
  const navigate = useNavigate();
  const { campaignId, uniqueToken, accessHash } = useParams<{
    campaignId: string;
    uniqueToken: string;
    accessHash: string;
  }>();

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [surveyData, setSurveyData] = useState<CustomerSurveyData | null>(null);
  const [contactInfo, setContactInfo] = useState<{ email?: string; phone?: string } | null>(null);
  const [rewardInfo, setRewardInfo] = useState<any>(null);
  const [campaignInfo, setCampaignInfo] = useState<any>(null);
  const [answers, setAnswers] = useState<Record<string, CustomerAnswer>>({});

  // Fetch survey on mount
  useEffect(() => {
    let isMounted = true;

    const fetchSurvey = async () => {
      if (!campaignId || !uniqueToken || !accessHash) {
        setError('Invalid survey link. Please check the URL and try again.');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const response = await getCustomerSurvey(campaignId, uniqueToken, accessHash);

        if (!isMounted) return;

        if (response.success && response.data?.survey) {
          setSurveyData(response.data.survey);
          
          // Store contact information for later use in reward claiming
          if (response.data.contact) {
            setContactInfo({
              email: response.data.contact.email,
              phone: response.data.contact.phone,
            });
          }
          
          // Store reward info from survey response
          if (response.data.reward_info) {
            setRewardInfo(response.data.reward_info);
          }
          
          // Store campaign info for platform details
          if (response.data.campaign) {
            setCampaignInfo(response.data.campaign);
          }
          
          console.log('Survey loaded successfully');
          console.log('Reward info:', response.data.reward_info);
          console.log('Campaign info:', response.data.campaign);
        } else {
          throw new Error(response.message || response.error || 'Failed to load survey');
        }
      } catch (err) {
        if (!isMounted) return;
        console.error('Error loading survey:', err);
        setError(
          err instanceof Error
            ? err.message
            : 'Unable to load survey. The link may be invalid or expired.'
        );
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchSurvey();

    return () => {
      isMounted = false;
    };
  }, [campaignId, uniqueToken, accessHash]);

  // Handlers
  const handleAnswerChange = (questionId: string, answer: string | number | string[]) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        question_id: questionId,
        answer_text: typeof answer === 'string' ? answer : '',
        answer_value: typeof answer === 'number' ? answer : null,
      },
    }));
  };

  const handleSubmit = async () => {
    if (!campaignId || !uniqueToken || !accessHash || !surveyData) {
      setError('Missing required information to submit survey');
      return;
    }

    // Validate required questions are answered
    const missingAnswers = surveyData.questions
      .filter((q) => q.is_mandatory && !answers[q.id])
      .map((q) => q.question_text);

    if (missingAnswers.length > 0) {
      setError('Please answer all required questions before submitting.');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      // Convert answers to array format
      const answersArray = Object.values(answers);

      console.log('📋 Submitting survey answers:', answersArray);

      // Submit the survey
      const response = await submitCustomerSurvey(
        campaignId,
        uniqueToken,
        accessHash,
        { answers: answersArray }
      );

      if (response.success) {
        console.log('✅ Survey submitted successfully');
        
        // Mock review data for now (will be replaced with actual review generation)
        const mockReview = {
          review_text: "Working with this company has been a fantastic experience from start to finish. The team is incredibly professional, responsive, and dedicated to delivering high-quality results. I'm thrilled with the outcome and would highly recommend their services to anyone looking for excellence and reliability.",
          platform: campaignInfo?.platform || surveyData.campaign_name || 'Google',
          platform_url: campaignInfo?.platform_link || 'https://www.google.com/maps',
          campaign_id: campaignId,
        };

        console.log('Review data prepared:', {
          platform: mockReview.platform,
          platform_url: mockReview.platform_url,
          campaign_id: mockReview.campaign_id,
        });

        // Prepare reward details from survey data
        const rewardDetails = rewardInfo?.available ? {
          reward_type: rewardInfo.type,
          reward_value: rewardInfo.type === 'cash' 
            ? `${rewardInfo.currency} ${rewardInfo.amount}`
            : rewardInfo.coupon_code || '15% off',
        } : null;

        navigate('/review-ready', {
          state: {
            review: mockReview,
            campaignId,
            uniqueToken,
            accessHash,
            contactInfo, // Pass contact info for reward claiming
            rewardDetails, // Pass reward details from survey
          },
        });
      } else {
        throw new Error(response.message || 'Failed to submit survey');
      }
    } catch (err) {
      console.error('❌ Error submitting survey:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to submit survey. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render compact question based on type
  const renderQuestion = (question: CustomerSurveyQuestion) => {
    const answer = answers[question.id];
    const currentTextAnswer = answer?.answer_text || '';
    const currentValueAnswer = answer?.answer_value || 0;

    switch (question.answer_type) {
      case 'rating':
        return (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1 }}>
            <Rating
              value={currentValueAnswer}
              onChange={(_, value) => {
                if (value !== null) {
                  handleAnswerChange(question.id, value);
                }
              }}
              size="large"
              icon={<Star fontSize="inherit" />}
              emptyIcon={<Star fontSize="inherit" />}
              sx={{
                '& .MuiRating-iconFilled': {
                  color: '#FBBF24',
                },
                '& .MuiRating-iconEmpty': {
                  color: '#E5E7EB',
                },
              }}
            />
          </Box>
        );

      case 'multiple_choice':
        return (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              mt: 1,
            }}
          >
            {question.options?.map((option, index) => (
              <Button
                key={index}
                variant={currentTextAnswer === option ? 'contained' : 'outlined'}
                onClick={() => handleAnswerChange(question.id, option)}
                size="small"
                sx={{
                  textTransform: 'none',
                  borderRadius: '8px',
                  minWidth: 'auto',
                  px: 2.5,
                  py: 0.75,
                  fontFamily: 'Inter',
                  fontWeight: 500,
                  ...(currentTextAnswer === option ? {
                    backgroundColor: '#4F46E5',
                    '&:hover': {
                      backgroundColor: '#4338CA',
                    },
                  } : {
                    borderColor: '#D1D5DB',
                    color: '#374151',
                    '&:hover': {
                      borderColor: '#4F46E5',
                      backgroundColor: '#EEF2FF',
                    },
                  }),
                }}
              >
                {option}
              </Button>
            ))}
          </Box>
        );

      case 'yes_no':
        return (
          <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
            <Button
              variant={currentTextAnswer === 'yes' ? 'contained' : 'outlined'}
              onClick={() => handleAnswerChange(question.id, 'yes')}
              size="medium"
              sx={{
                minWidth: '100px',
                textTransform: 'none',
                borderRadius: '8px',
                fontFamily: 'Inter',
                fontWeight: 500,
                py: 1,
                ...(currentTextAnswer === 'yes' ? {
                  backgroundColor: '#4F46E5',
                  '&:hover': {
                    backgroundColor: '#4338CA',
                  },
                } : {
                  borderColor: '#D1D5DB',
                  color: '#374151',
                  '&:hover': {
                    borderColor: '#4F46E5',
                    backgroundColor: '#EEF2FF',
                  },
                }),
              }}
            >
              Yes
            </Button>
            <Button
              variant={currentTextAnswer === 'no' ? 'contained' : 'outlined'}
              onClick={() => handleAnswerChange(question.id, 'no')}
              size="medium"
              sx={{
                minWidth: '100px',
                textTransform: 'none',
                borderRadius: '8px',
                fontFamily: 'Inter',
                fontWeight: 500,
                py: 1,
                ...(currentTextAnswer === 'no' ? {
                  backgroundColor: '#4F46E5',
                  '&:hover': {
                    backgroundColor: '#4338CA',
                  },
                } : {
                  borderColor: '#D1D5DB',
                  color: '#374151',
                  '&:hover': {
                    borderColor: '#4F46E5',
                    backgroundColor: '#EEF2FF',
                  },
                }),
              }}
            >
              No
            </Button>
          </Box>
        );

      case 'text':
        return (
          <Box sx={{ mt: 1 }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Type your answer here..."
              value={currentTextAnswer}
              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#F9FAFB',
                  borderRadius: '8px',
                  fontFamily: 'Inter',
                  fontSize: '0.9375rem',
                  '& fieldset': {
                    borderColor: '#E5E7EB',
                  },
                  '&:hover fieldset': {
                    borderColor: '#D1D5DB',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#4F46E5',
                    borderWidth: '1px',
                  },
                },
              }}
            />
          </Box>
        );

      case 'number':
        return (
          <Box sx={{ mt: 1 }}>
            <TextField
              fullWidth
              type="number"
              placeholder="Enter a number..."
              value={currentValueAnswer || ''}
              onChange={(e) => handleAnswerChange(question.id, Number(e.target.value))}
              sx={{
                maxWidth: '250px',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#F9FAFB',
                  borderRadius: '8px',
                  fontFamily: 'Inter',
                  '& fieldset': {
                    borderColor: '#E5E7EB',
                  },
                  '&:hover fieldset': {
                    borderColor: '#D1D5DB',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#4F46E5',
                    borderWidth: '1px',
                  },
                },
              }}
            />
          </Box>
        );

      default:
        return null;
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <PageContainer>
        <ContentContainer>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '60vh',
              gap: 2,
            }}
          >
            <CircularProgress size={48} sx={{ color: '#4F46E5' }} />
            <Typography variant="h6" color="text.secondary" sx={{ fontFamily: 'Inter' }}>
              Loading survey...
            </Typography>
          </Box>
        </ContentContainer>
      </PageContainer>
    );
  }

  // Error state
  if (error || !surveyData) {
    return (
      <PageContainer>
        <ContentContainer>
          <Header>
            <LogoIcon>
              <svg fill="none" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M39.5563 34.1455V13.8546C39.5563 15.708 36.8773 17.3437 32.7927 18.3189C30.2914 18.916 27.263 19.2655 24 19.2655C20.737 19.2655 17.7086 18.916 15.2073 18.3189C11.1227 17.3437 8.44365 15.708 8.44365 13.8546V34.1455C8.44365 35.9988 11.1227 37.6346 15.2073 38.6098C17.7086 39.2069 20.737 39.5564 24 39.5564C27.263 39.5564 30.2914 39.2069 32.7927 38.6098C36.8773 37.6346 39.5563 35.9988 39.5563 34.1455Z"
                  fill="currentColor"
                />
                <path
                  clipRule="evenodd"
                  d="M10.4485 13.8519C10.4749 13.9271 10.6203 14.246 11.379 14.7361C12.298 15.3298 13.7492 15.9145 15.6717 16.3735C18.0007 16.9296 20.8712 17.2655 24 17.2655C27.1288 17.2655 29.9993 16.9296 32.3283 16.3735C34.2508 15.9145 35.702 15.3298 36.621 14.7361C37.3796 14.246 37.5251 13.9271 37.5515 13.8519C37.5287 13.7876 37.4333 13.5973 37.0635 13.2931C36.5266 12.8516 35.6288 12.3647 34.343 11.9175C31.79 11.0295 28.1333 10.4437 24 10.4437C19.8667 10.4437 16.2099 11.0295 13.657 11.9175C12.3712 12.3647 11.4734 12.8516 10.9365 13.2931C10.5667 13.5973 10.4713 13.7876 10.4485 13.8519ZM37.5563 18.7877C36.3176 19.3925 34.8502 19.8839 33.2571 20.2642C30.5836 20.9025 27.3973 21.2655 24 21.2655C20.6027 21.2655 17.4164 20.9025 14.7429 20.2642C13.1498 19.8839 11.6824 19.3925 10.4436 18.7877V34.1275C10.4515 34.1545 10.5427 34.4867 11.379 35.027C12.298 35.6207 13.7492 36.2054 15.6717 36.6644C18.0007 37.2205 20.8712 37.5564 24 37.5564C27.1288 37.5564 29.9993 37.2205 32.3283 36.6644C34.2508 36.2054 35.702 35.6207 36.621 35.027C37.4573 34.4867 37.5485 34.1546 37.5563 34.1275V18.7877ZM41.5563 13.8546V34.1455C41.5563 36.1078 40.158 37.5042 38.7915 38.3869C37.3498 39.3182 35.4192 40.0389 33.2571 40.5551C30.5836 41.1934 27.3973 41.5564 24 41.5564C20.6027 41.5564 17.4164 41.1934 14.7429 40.5551C12.5808 40.0389 10.6502 39.3182 9.20848 38.3869C7.84205 37.5042 6.44365 36.1078 6.44365 34.1455L6.44365 13.8546C6.44365 12.2684 7.37223 11.0454 8.39581 10.2036C9.43325 9.3505 10.8137 8.67141 12.343 8.13948C15.4203 7.06909 19.5418 6.44366 24 6.44366C28.4582 6.44366 32.5797 7.06909 35.657 8.13948C37.1863 8.67141 38.5667 9.3505 39.6042 10.2036C40.6278 11.0454 41.5563 12.2684 41.5563 13.8546Z"
                  fill="currentColor"
                  fillRule="evenodd"
                />
              </svg>
            </LogoIcon>
            <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'Inter', color: '#111827' }}>
              RiteReview
            </Typography>
          </Header>

          <Alert severity="error" sx={{ mt: 4, borderRadius: '12px', fontFamily: 'Inter' }}>
            <Typography variant="h6" sx={{ mb: 1, fontFamily: 'Inter', fontWeight: 600 }}>
              Unable to Load Survey
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'Inter' }}>{error || 'An unexpected error occurred'}</Typography>
          </Alert>
        </ContentContainer>
      </PageContainer>
    );
  }

  // Main survey UI - Show all questions at once
  return (
    <PageContainer>
      <ContentContainer>
        {/* Header */}
        <Header>
          <LogoIcon>
            <svg fill="none" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M39.5563 34.1455V13.8546C39.5563 15.708 36.8773 17.3437 32.7927 18.3189C30.2914 18.916 27.263 19.2655 24 19.2655C20.737 19.2655 17.7086 18.916 15.2073 18.3189C11.1227 17.3437 8.44365 15.708 8.44365 13.8546V34.1455C8.44365 35.9988 11.1227 37.6346 15.2073 38.6098C17.7086 39.2069 20.737 39.5564 24 39.5564C27.263 39.5564 30.2914 39.2069 32.7927 38.6098C36.8773 37.6346 39.5563 35.9988 39.5563 34.1455Z"
                fill="currentColor"
              />
              <path
                clipRule="evenodd"
                d="M10.4485 13.8519C10.4749 13.9271 10.6203 14.246 11.379 14.7361C12.298 15.3298 13.7492 15.9145 15.6717 16.3735C18.0007 16.9296 20.8712 17.2655 24 17.2655C27.1288 17.2655 29.9993 16.9296 32.3283 16.3735C34.2508 15.9145 35.702 15.3298 36.621 14.7361C37.3796 14.246 37.5251 13.9271 37.5515 13.8519C37.5287 13.7876 37.4333 13.5973 37.0635 13.2931C36.5266 12.8516 35.6288 12.3647 34.343 11.9175C31.79 11.0295 28.1333 10.4437 24 10.4437C19.8667 10.4437 16.2099 11.0295 13.657 11.9175C12.3712 12.3647 11.4734 12.8516 10.9365 13.2931C10.5667 13.5973 10.4713 13.7876 10.4485 13.8519ZM37.5563 18.7877C36.3176 19.3925 34.8502 19.8839 33.2571 20.2642C30.5836 20.9025 27.3973 21.2655 24 21.2655C20.6027 21.2655 17.4164 20.9025 14.7429 20.2642C13.1498 19.8839 11.6824 19.3925 10.4436 18.7877V34.1275C10.4515 34.1545 10.5427 34.4867 11.379 35.027C12.298 35.6207 13.7492 36.2054 15.6717 36.6644C18.0007 37.2205 20.8712 37.5564 24 37.5564C27.1288 37.5564 29.9993 37.2205 32.3283 36.6644C34.2508 36.2054 35.702 35.6207 36.621 35.027C37.4573 34.4867 37.5485 34.1546 37.5563 34.1275V18.7877ZM41.5563 13.8546V34.1455C41.5563 36.1078 40.158 37.5042 38.7915 38.3869C37.3498 39.3182 35.4192 40.0389 33.2571 40.5551C30.5836 41.1934 27.3973 41.5564 24 41.5564C20.6027 41.5564 17.4164 41.1934 14.7429 40.5551C12.5808 40.0389 10.6502 39.3182 9.20848 38.3869C7.84205 37.5042 6.44365 36.1078 6.44365 34.1455L6.44365 13.8546C6.44365 12.2684 7.37223 11.0454 8.39581 10.2036C9.43325 9.3505 10.8137 8.67141 12.343 8.13948C15.4203 7.06909 19.5418 6.44366 24 6.44366C28.4582 6.44366 32.5797 7.06909 35.657 8.13948C37.1863 8.67141 38.5667 9.3505 39.6042 10.2036C40.6278 11.0454 41.5563 12.2684 41.5563 13.8546Z"
                fill="currentColor"
                fillRule="evenodd"
              />
            </svg>
          </LogoIcon>
          <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'Inter', color: '#111827' }}>
            {surveyData.business_name || 'RiteReview'}
          </Typography>
        </Header>

        {/* Title */}
        <Box sx={{ textAlign: 'center', px: 2, py: 3 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              mb: 1,
              fontFamily: 'Inter',
              color: '#111827',
            }}
          >
            {surveyData.survey_name}
          </Typography>
          {surveyData.description && (
            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Inter' }}>
              {surveyData.description}
            </Typography>
          )}
        </Box>

        {/* All Questions */}
        <Box sx={{ px: 2, mb: 3 }}>
          {surveyData.questions.map((question, index) => (
            <Card
              key={question.id}
              sx={{
                mb: 2,
                borderRadius: '12px',
                border: '1px solid #E5E7EB',
                boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
                backgroundColor: '#FFFFFF',
              }}
            >
              <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1.5,
                    fontFamily: 'Inter',
                    color: '#111827',
                    fontSize: '0.9375rem',
                  }}
                >
                  {index + 1}. {question.question_text}
                  {question.is_mandatory && (
                    <Typography component="span" sx={{ color: '#EF4444', ml: 0.5 }}>
                      *
                    </Typography>
                  )}
                </Typography>
                {renderQuestion(question)}
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Submit Button */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            px: 2,
            pb: 4,
          }}
        >
          <Button
            onClick={handleSubmit}
            variant="contained"
            size="large"
            disabled={isSubmitting}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              px: 6,
              py: 1.5,
              fontWeight: 600,
              minWidth: '200px',
              fontFamily: 'Inter',
              backgroundColor: '#4F46E5',
              '&:hover': {
                backgroundColor: '#4338CA',
              },
              '&:disabled': {
                backgroundColor: '#D1D5DB',
                color: '#9CA3AF',
              },
            }}
          >
            {isSubmitting ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: 'inherit' }} />
                Submitting...
              </>
            ) : (
              'Submit Survey'
            )}
          </Button>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mx: 2, mb: 3, borderRadius: '12px', fontFamily: 'Inter' }}>
            {error}
          </Alert>
        )}
      </ContentContainer>
    </PageContainer>
  );
};

export default CustomerSurvey;

